import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import {
  HERO,
  BELIEF,
  AREAS_OF_EXPERTISE,
  AUDIENCE_ROUTER,
  PARTNERS,
  RECOGNITION,
  BRAND,
} from "@/lib/content";
import SectionEyebrow from "@/components/site/SectionEyebrow";
import RotatingTagline from "@/components/site/RotatingTagline";
import AreaCard from "@/components/site/AreaCard";
import ConsultationBand from "@/components/site/ConsultationBand";

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* 1. Hero (typographic, no photo) */}
      <section data-testid="home-hero" className="wt-section pt-14 md:pt-24">
        <div className="wt-container">
          <SectionEyebrow>
            School Climate &amp; Community Partnership Consultant
          </SectionEyebrow>
          <h1 className="wt-h1 mt-5 max-w-4xl">{HERO.headline}</h1>
          <RotatingTagline items={HERO.rotators} />
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
            <Link
              to="/book-consultation"
              data-testid="hero-primary-cta"
              className="wt-btn-primary text-base h-12 px-6"
            >
              {HERO.primaryCta}
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-secondary-cta"
              className="wt-btn-outline text-base h-12 px-6"
            >
              {HERO.secondaryCta}
            </Link>
          </div>
          <p className="mt-6 text-sm wt-text-muted max-w-xl">
            {BRAND.rolesLine}
          </p>
        </div>
      </section>

      {/* 2. Audience Router */}
      <section
        data-testid="home-audience-router"
        className="wt-section"
        style={{ backgroundColor: "#FBF8F1", borderTop: "1px solid var(--wt-border)", borderBottom: "1px solid var(--wt-border)" }}
      >
        <div className="wt-container">
          <div className="max-w-2xl">
            <SectionEyebrow>Choose your path</SectionEyebrow>
            <h2 className="wt-h2 mt-4">Which best describes you?</h2>
            <p className="wt-lead mt-4">
              The work and the language shift depending on who is in front of me. Start here so
              you only see what applies to you.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {AUDIENCE_ROUTER.map((card, i) => (
              <Link
                key={card.id}
                to={card.to}
                data-testid={`audience-card-${card.id}`}
                className="wt-service-card hover:translate-y-[-2px] group"
              >
                <div className="flex items-center justify-between">
                  <span className="wt-eyebrow">{`Path 0${i + 1}`}</span>
                  <ArrowUpRight
                    size={18}
                    className="wt-primary transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    aria-hidden="true"
                  />
                </div>
                <h3 className="wt-h3 mt-2">{card.label}</h3>
                <p className="wt-body wt-text-muted mt-1">{card.description}</p>
                <p className="mt-auto pt-4 text-sm font-semibold wt-primary">
                  See services for {card.label.toLowerCase()}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Five Areas of Expertise */}
      <section data-testid="home-areas" className="wt-section">
        <div className="wt-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="max-w-2xl">
              <SectionEyebrow>Areas of Expertise</SectionEyebrow>
              <h2 className="wt-h2 mt-4">Five areas of focused work.</h2>
            </div>
            <Link
              to="/services"
              data-testid="home-areas-link"
              className="wt-link text-sm font-semibold"
            >
              See all areas in detail
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {AREAS_OF_EXPERTISE.map((a, i) => (
              <AreaCard key={a.id} area={a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* 4. Proof band */}
      <section
        data-testid="home-proof-band"
        className="wt-section"
        style={{ backgroundColor: "#FBF8F1" }}
      >
        <div className="wt-container">
          <div className="max-w-2xl">
            <SectionEyebrow>Selected schools and partners</SectionEyebrow>
            <h2 className="wt-h2 mt-4">A decade of work in real rooms.</h2>
            <p className="wt-lead mt-4">
              Selected schools, higher education partners, and community partners Wanakee has
              served. A fuller list lives in the one-pager and is available on request.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7">
              <p className="wt-eyebrow">Schools</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {PARTNERS.schools.map((s) => (
                  <span key={s} className="wt-pill" data-testid="proof-school">
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5 space-y-6">
              <div>
                <p className="wt-eyebrow">Higher Education</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {PARTNERS.higherEducation.map((s) => (
                    <span key={s} className="wt-pill" data-testid="proof-higher-ed">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="wt-eyebrow">Community, Civic &amp; Corporate</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {PARTNERS.community.map((s) => (
                    <span key={s} className="wt-pill" data-testid="proof-community">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <p className="wt-eyebrow">Recognition</p>
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {RECOGNITION.map((r) => (
                <li key={r} className="wt-card p-4 text-sm wt-body" data-testid="proof-recognition">
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full mr-2 align-middle"
                    style={{ backgroundColor: "var(--wt-gold)" }}
                    aria-hidden="true"
                  />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 5. Belief statement */}
      <section data-testid="home-belief" className="wt-section">
        <div className="wt-container">
          <div className="max-w-3xl">
            <SectionEyebrow>Belief</SectionEyebrow>
            <p
              className="mt-5 font-serif leading-tight"
              style={{
                fontSize: "clamp(1.5rem, 1.05rem + 2vw, 2.25rem)",
                color: "var(--wt-ground)",
                letterSpacing: "-0.015em",
              }}
            >
              <Sparkles
                size={18}
                aria-hidden="true"
                className="inline-block mr-2 align-baseline"
                style={{ color: "var(--wt-gold)" }}
              />
              {BELIEF}
            </p>
          </div>
        </div>
      </section>

      {/* 6. Closing CTA */}
      <ConsultationBand />
    </div>
  );
}
